import { useEffect, useRef, useState } from "react"
import { ChevronDown } from "lucide-react"
import { Tooltip } from "../ui/tooltip"

export const SEVERANCE_BADGE_LABEL = "Severance"
export const SEVERANCE_SUMMARY_LABEL = "Severance"
export const SEVERANCE_DEFAULT_REASON = "Severance agreement offered"
export const SEVERANCE_STATUS_LABEL = "Severance status"
export const SEVERANCE_STATUS_PENDING = "pending"
export const SEVERANCE_STATUS_INTERESTED = "interested"
export const SEVERANCE_STATUS_DECLINED = "declined"

export const SEVERANCE_STATUS_OPTIONS = [
  { value: SEVERANCE_STATUS_PENDING, label: "Pending" },
  { value: SEVERANCE_STATUS_INTERESTED, label: "Interested" },
  { value: SEVERANCE_STATUS_DECLINED, label: "Declined" },
] as const

interface SeveranceBadgeProps {
  readonly reason?: string | null
}

export function SeveranceBadge({ reason }: SeveranceBadgeProps) {
  return (
    <Tooltip content={reason || SEVERANCE_DEFAULT_REASON} side="bottom">
      <span
        aria-label={SEVERANCE_BADGE_LABEL}
        className="relative inline-block h-5 w-5 flex-shrink-0 overflow-hidden rounded-sm border border-violet-300 bg-violet-100"
      >
        <span
          aria-hidden
          className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 text-base font-semibold leading-none text-violet-600"
        >
          s
        </span>
      </span>
    </Tooltip>
  )
}

interface SeveranceSummaryProps {
  readonly isSeverance?: boolean
  readonly reason?: string | null
}

export function SeveranceSummary({ isSeverance, reason }: SeveranceSummaryProps) {
  if (!isSeverance) {
    return null
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-sm font-medium text-slate-900">{SEVERANCE_SUMMARY_LABEL}</p>
      <p className="mt-2 text-sm text-slate-700">{reason || SEVERANCE_DEFAULT_REASON}</p>
    </div>
  )
}

interface SeveranceStatusSelectProps {
  readonly value?: string | null
  readonly onChange: (value: string) => void
}

export function SeveranceStatusSelect({ value, onChange }: SeveranceStatusSelectProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const selected = SEVERANCE_STATUS_OPTIONS.find((option) => option.value === value)

  useEffect(() => {
    if (!open) {
      return
    }
    const close = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", close)
    return () => document.removeEventListener("mousedown", close)
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label={SEVERANCE_STATUS_LABEL}
        aria-expanded={open}
        className="flex h-8 min-w-[10rem] items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 text-sm shadow-sm hover:bg-slate-50"
        onClick={() => setOpen((current) => !current)}
      >
        <span className={selected ? "text-slate-900" : "text-slate-500"}>
          {selected?.label ?? SEVERANCE_STATUS_LABEL}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <ul className="absolute left-0 z-50 mt-1 w-full rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {SEVERANCE_STATUS_OPTIONS.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                className={`flex w-full px-3 py-2 text-left text-sm hover:bg-slate-100 ${option.value === value ? "bg-slate-100 font-medium text-slate-900" : "text-slate-700"}`}
                onClick={() => {
                  onChange(option.value)
                  setOpen(false)
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
